<template>
  <div class="sci-page-wrapper" style="font-family: 'Inter', sans-serif; background: #f4f6f9; min-height: 100vh;">
    <div class="max-w-7xl mx-auto px-4 py-8 space-y-6">
      
      <!-- HEADER -->
      <div class="sci-page-header pb-2">
        <h1 class="text-3xl font-black text-slate-800 uppercase tracking-tight">Usuários</h1>
        <p class="text-sm text-slate-500 font-medium mt-1">Controle de acessos, grupos e permissões do sistema</p>
      </div>

      <!-- FILTROS DE PESQUISA -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="px-6 py-3 flex items-center gap-3" style="background-color: #333333;">
              <v-icon color="orange" small>mdi-filter-variant</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">FILTROS DE BUSCA</h2>
          </div>
          <div class="p-6">
              <v-row dense>
                  <v-col cols="12" md="4">
                      <label class="field-label-main">Grupo de Acesso</label>
                      <v-autocomplete
                        v-model="selectedGroup"
                        :items="groups"
                        item-text="group_name"
                        item-value="group_id"
                        placeholder="TODOS OS GRUPOS"
                        outlined dense hide-details
                        class="field-input-main"
                        clearable
                      ></v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="4">
                      <label class="field-label-main">Município / Prefeitura</label>
                      <v-autocomplete
                        v-model="selectedPrefecture"
                        :items="prefectures"
                        item-text="prefecture_social_name"
                        item-value="prefecture_id"
                        placeholder="TODAS AS PREFEITURAS"
                        outlined dense hide-details
                        class="field-input-main"
                        clearable
                      ></v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="4">
                      <label class="field-label-main">Pesquisa Rápida</label>
                      <v-text-field
                        v-model="search"
                        placeholder="NOME OU LOGIN..."
                        outlined dense hide-details
                        prepend-inner-icon="mdi-magnify"
                        class="field-input-main"
                      ></v-text-field>
                  </v-col>
              </v-row>
          </div>
      </section>

      <!-- GRID DE USUÁRIOS -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div style="background-color: #333333; display: flex; align-items: center; padding: 10px 20px; width: 100%; box-sizing: border-box;">

              <!-- TÍTULO -->
              <div style="display: flex; align-items: center; gap: 8px; flex-shrink: 0;">
                <v-icon color="orange" small>mdi-account-group</v-icon>
                <span style="color: white; font-weight: 700; font-size: 11px; text-transform: uppercase; letter-spacing: 0.1em;">USUÁRIOS CADASTRADOS</span>
              </div>

              <!-- AÇÕES FIXADAS À DIREITA -->
              <div style="margin-left: auto; display: flex; align-items: center; gap: 12px; flex-shrink: 0;">

                <!-- BOTÃO NOVO -->
                <button
                  @click="$router.push('/users-manager/create')"
                  style="height: 32px; width: auto; white-space: nowrap; flex-shrink: 0; background-color: #f37021; color: white; display: inline-flex; align-items: center; gap: 6px; border-radius: 8px; padding: 0 14px; font-size: 10px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; border: none; cursor: pointer; transition: background-color 0.2s;"
                  onmouseover="this.style.backgroundColor='#e0601a'"
                  onmouseout="this.style.backgroundColor='#f37021'"
                >
                  <v-icon color="white" x-small>mdi-plus</v-icon>
                  NOVO
                </button>

              </div>
          </div>

          <div class="overflow-x-auto">
             <v-data-table
                :headers="headers"
                :items="filteredData"
                :search="search"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                @page-count="pageCount = $event"
                :loading="loader"
                loading-text="CARREGANDO USUÁRIOS..."
                class="custom-table"
                hide-default-footer
              >
                <!-- FOTO / AVATAR -->
                <template v-slot:item.user_photo="{ item }">
                  <div class="py-2 flex justify-center">
                    <v-avatar size="42" class="border-2 border-slate-100 shadow-sm bg-white">
                      <img
                        v-if="item.user_photo"
                        :src="'https://api.sigfrotagov.com.br/uploads/' + item.user_photo"
                        :alt="item.user_login"
                        class="object-cover"
                      />
                      <v-icon v-else color="#cbd5e0" size="24">mdi-account</v-icon>
                    </v-avatar>
                  </div>
                </template>

                <!-- USUÁRIO / LOGIN -->
                <template v-slot:item.user_login="{ item }">
                  <div class="py-4">
                    <div class="flex flex-col">
                      <span class="text-sm font-bold text-slate-700 uppercase tracking-tight">{{ item.user_login }}</span>
                      <span class="text-[10px] font-bold text-slate-400 lowercase tracking-tight">{{ item.user_email || 'sem e-mail vinculado' }}</span>
                    </div>
                  </div>
                </template>

                <!-- GRUPO DE ACESSO -->
                <template v-slot:item.group_name="{ item }">
                  <span class="px-2 py-1 rounded bg-slate-100 border border-slate-200 text-[9px] font-black text-slate-500 uppercase tracking-widest">
                    {{ getGroupName(item.group_id) }}
                  </span>
                </template>

                <!-- VÍNCULO -->
                <template v-slot:item.vinculo="{ item }">
                   <div class="flex items-center gap-2">
                      <v-icon x-small color="slate-400">mdi-link-variant</v-icon>
                      <span class="text-[10px] font-bold text-slate-600 uppercase">{{ getVinculoDesc(item) }}</span>
                   </div>
                </template>

                <!-- AÇÕES -->
                <template v-slot:item.action="{ item }">
                  <div class="flex justify-end gap-2 px-4">
                    <button 
                      @click="$router.push(`/users-manager/edit/${item.user_id}`)"
                      class="p-2 hover:bg-orange-50 text-orange-600 rounded-lg transition-colors"
                    >
                      <v-icon small color="orange">mdi-pencil</v-icon>
                    </button>
                    <button 
                      @click="deleteItem(item.user_id)"
                      class="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors"
                    >
                      <v-icon small color="red">mdi-delete</v-icon>
                    </button>
                  </div>
                </template>

                <template v-slot:no-data>
                  <div class="py-12 flex flex-col items-center justify-center text-slate-400">
                    <v-icon size="48" color="slate-100">mdi-account-search-outline</v-icon>
                    <p class="mt-2 text-[10px] font-bold uppercase tracking-widest">Nenhum usuário encontrado</p>
                  </div>
                </template>
              </v-data-table>

              <!-- Custom Pagination Footer -->
              <div class="bg-slate-50 px-6 py-6 border-t border-slate-100 flex flex-col items-center justify-center gap-4 text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                  <span>Exibindo {{ paginationStart }} de {{ filteredData.length }} registros</span>
                  
                  <div class="flex gap-2">
                      <button 
                        @click="page > 1 ? page-- : null" 
                        :disabled="page === 1"
                        class="px-4 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        Anterior
                      </button>
                      
                      <button 
                        v-for="p in visiblePages" 
                        :key="p"
                        @click="page = p"
                        :class="p === page ? 'bg-slate-700 text-white border-slate-700 shadow-sm' : 'bg-white border-slate-200 hover:bg-slate-100'"
                        class="w-8 h-8 border rounded-lg transition-all flex items-center justify-center"
                      >
                        {{ p }}
                      </button>

                      <button 
                        @click="page < pageCount ? page++ : null" 
                        :disabled="page === pageCount"
                        class="px-4 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        Próximo
                      </button>
                  </div>
              </div>
          </div>
      </section>

      <!-- Snackbar -->
      <v-snackbar
        v-model="snack.show"
        :color="snack.color"
        timeout="3000"
        top
        elevation="24"
        class="custom-snack"
      >
        <div class="flex items-center gap-3">
          <v-icon color="white" small>{{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
          <span class="text-xs font-bold uppercase tracking-widest">{{ snack.message }}</span>
        </div>
      </v-snackbar>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      search: "",
      loader: true,
      data: [],
      snack: {
        show: false,
        message: "",
        color: ""
      },
      groups: [],
      selectedGroup: null,
      prefectures: [],
      selectedPrefecture: null,
      headers: [
        { text: "FOTO", value: "user_photo", sortable: false, align: "center", width: "80px" },
        { text: "USUÁRIO / ACESSO", value: "user_login", sortable: true },
        { text: "GRUPO", value: "group_name", sortable: true },
        { text: "VÍNCULO", value: "vinculo", sortable: false },
        { text: "AÇÕES", align: "right", value: "action", sortable: false }
      ],
      page: 1,
      pageCount: 0,
      itemsPerPage: 10,
    };
  },
  computed: {
    paginationStart() {
      return this.filteredData.length > 0 ? (this.page - 1) * this.itemsPerPage + 1 : 0;
    },
    filteredData() {
      let filtered = this.data;
      if (this.selectedGroup) {
        filtered = filtered.filter(u => u.group_id == this.selectedGroup);
      }
      if (this.selectedPrefecture) {
        filtered = filtered.filter(u => {
          return u.prefecture_id == this.selectedPrefecture || 
                 (u.table_key === 'prefectures' && u.table_id == this.selectedPrefecture);
        });
      }
      return filtered;
    },
    visiblePages() {
      const pages = [];
      const totalVisible = 5;
      let start = Math.max(1, this.page - Math.floor(totalVisible / 2));
      let end = Math.min(this.pageCount, start + totalVisible - 1);
      if (end - start + 1 < totalVisible) start = Math.max(1, end - totalVisible + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    getGroupName(id) {
      const g = this.groups.find(v => v.group_id == id);
      return g ? g.group_name : 'Sem Grupo';
    },
    getVinculoDesc(item) {
      if (item.group_id === 1 && item.prefectures && item.prefectures.length > 0) {
        return `${item.prefectures.length} Municípios`;
      }
      return item.table_name || 'Vínculo Direto';
    },
    initialize() {
      this.loader = true;
      this.$store.dispatch("setLoader", true);
      
      const user = this.$store.state.userData[0];
      const isRestrictedAdmin = user && (user.group_id === 1 || user.group_id === 5) && user.prefectures && user.prefectures.length > 0;

      this.$http.get("/users").then(r => {
        this.data = r.data;
        this.loader = false;
        this.$store.dispatch("setLoader", false);
      }).catch(e => {
        this.loader = false;
        this.snack.show = true;
        this.snack.message = e.message;
        this.snack.color = "error";
        this.$store.dispatch("setLoader", false);
      });

      this.$http.get("/access-groups").then(r => { this.groups = r.data; });
      this.$http.get("/prefectures").then(r => {
        this.prefectures = r.data;
        if (isRestrictedAdmin && !this.selectedPrefecture && this.prefectures.length > 0) {
          this.selectedPrefecture = this.prefectures[0].prefecture_id;
        }
      });
    },
    deleteItem(id) {
      if (confirm("Deseja realmente excluir este usuário? Esta ação não pode ser desfeita.")) {
        this.$store.dispatch("setLoader", true);
        this.$http.delete(`/users/${id}`).then(r => {
          this.snack.show = true;
          this.snack.message = r.data.result || "Usuário removido!";
          this.snack.color = "success";
          this.initialize();
        }).catch(e => {
          this.snack.show = true;
          this.snack.message = e.message;
          this.snack.color = "error";
          this.$store.dispatch("setLoader", false);
        });
      }
    }
  }
};
</script>

<style scoped>
.field-label-main {
  display: block;
  font-size: 9px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
}

.field-input-main >>> .v-input__control .v-input__slot {
  background-color: #f8fafc !important;
  border-radius: 8px !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
}

.custom-table ::v-deep table { background-color: transparent !important; border-collapse: separate; border-spacing: 0; }
.custom-table ::v-deep th { 
  color: #94a3b8 !important; 
  font-weight: 700 !important; 
  text-transform: uppercase !important; 
  font-size: 10px !important; 
  letter-spacing: 0.05em !important;
  background-color: #f8fafc !important;
  border-bottom: 1px solid #e2e8f0 !important;
  height: 48px !important;
}
.custom-table ::v-deep td { 
  border-bottom: 1px solid #f1f5f9 !important;
  color: #334155 !important;
  font-size: 13px !important;
}
.custom-snack >>> .v-snack__wrapper {
  border-radius: 10px !important;
}
</style>